import {
  ArrowLeft,
  RefreshCcw,
  Star,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useState,
} from "react";

import { supabase } from "../lib/supabase";

type FeedbackRow = {
  id: string;
  guest_name: string | null;
  email: string | null;
  phone: string | null;
  visit_date: string | null;
  overall_rating: number;
  food_rating: number | null;
  service_rating: number | null;
  atmosphere_rating: number | null;
  highlights: string | null;
  improvements: string | null;
  additional_comments: string | null;
  would_return: boolean | null;
  permission_to_contact: boolean;
  status: string;
  created_at: string;
};

const STATUSES = [
  "new",
  "reviewed",
  "follow_up",
  "resolved",
  "archived",
];

function Stars({
  value,
}: {
  value: number | null;
}) {
  if (value === null) {
    return (
      <span className="text-[#8d988d]">
        —
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-[#9d725c]">
      <Star
        size={12}
        fill="currentColor"
      />
      {value}/5
    </span>
  );
}

export function NovaCmsFeedback() {
  const [rows, setRows] =
    useState<FeedbackRow[]>([]);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: loadError } =
      await supabase
        .from("nova_guest_feedback")
        .select(
          "id,guest_name,email,phone,visit_date,overall_rating,food_rating,service_rating,atmosphere_rating,highlights,improvements,additional_comments,would_return,permission_to_contact,status,created_at",
        )
        .order("created_at", {
          ascending: false,
        });

    if (loadError) {
      console.error(
        "[Nova CMS] feedback load failed",
        loadError,
      );

      setError(loadError.message);
      setRows([]);
    } else {
      setRows(
        (data ?? []) as FeedbackRow[],
      );
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function updateStatus(
    id: string,
    status: string,
  ) {
    const { error: updateError } =
      await supabase
        .from("nova_guest_feedback")
        .update({ status })
        .eq("id", id);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    setRows((current) =>
      current.map((row) =>
        row.id === id
          ? {
              ...row,
              status,
            }
          : row,
      ),
    );
  }

  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      <header className="border-b border-[#b5bbae] bg-[#314a3b] text-[#f3ead7]">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-5 py-5 sm:px-8 lg:px-10">
          <button
            type="button"
            onClick={() => {
              window.location.href =
                "/nova-hq";
            }}
            className="flex items-center gap-3 text-[9px] font-semibold uppercase tracking-[0.18em]"
          >
            <ArrowLeft size={14} />
            Nova HQ
          </button>

          <img
            src="/images/nova/logodark.png"
            onError={(event) => {
              event.currentTarget.src =
                "/images/nova/novatransparent.png";
            }}
            alt="Nova Res Culina"
            className="h-14 w-auto object-contain"
          />
        </div>
      </header>

      <section className="mx-auto max-w-[1440px] px-5 py-10 sm:px-8 lg:px-10 lg:py-14">
        <div className="flex flex-col justify-between gap-7 lg:flex-row lg:items-end">
          <div className="max-w-3xl">
            <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
              Guest operations
            </p>

            <h1 className="mt-4 font-['Cormorant_Garamond'] text-5xl leading-none sm:text-6xl">
              Guest feedback
            </h1>

            <p className="mt-5 max-w-2xl text-sm leading-7 text-[#667067]">
              Private feedback from Nova
              guests. Review the experience,
              identify follow-ups and close
              the loop where needed.
            </p>
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={() =>
              void load()
            }
            className="flex h-11 items-center gap-3 border border-[#9d725c] px-4 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c] disabled:opacity-50"
          >
            <RefreshCcw size={14} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mt-8 border border-[#9d725c]/30 bg-[#9d725c]/8 px-5 py-4 text-xs text-[#7d5544]">
            {error}
          </div>
        )}

        <div className="mt-10">
          {loading &&
          rows.length === 0 ? (
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#718076]">
              Loading feedback…
            </p>
          ) : rows.length === 0 ? (
            <div className="border border-[#b5bbae] bg-[#f4efe4] p-10">
              <h2 className="font-['Cormorant_Garamond'] text-4xl">
                No feedback yet.
              </h2>

              <p className="mt-3 text-sm text-[#667067]">
                New guest responses will
                appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {rows.map((row) => (
                <article
                  key={row.id}
                  className="border border-[#b5bbae] bg-[#f4efe4] p-6 sm:p-7"
                >
                  <div className="grid gap-7 xl:grid-cols-[1fr_1fr_1.2fr_.7fr]">
                    <div>
                      <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#9d725c]">
                        Overall
                      </p>

                      <div className="mt-3 font-['Cormorant_Garamond'] text-4xl text-[#314a3b]">
                        {row.overall_rating}/5
                      </div>

                      <p className="mt-4 text-xs text-[#667067]">
                        {row.guest_name ||
                          "Anonymous guest"}
                      </p>

                      {row.visit_date && (
                        <p className="mt-1 text-xs text-[#667067]">
                          Visited{" "}
                          {row.visit_date}
                        </p>
                      )}

                      <p className="mt-1 text-[10px] text-[#8d988d]">
                        {new Date(
                          row.created_at,
                        ).toLocaleString(
                          "en-KE",
                        )}
                      </p>
                    </div>

                    <div className="space-y-2 text-xs">
                      <p>
                        Food:{" "}
                        <Stars
                          value={
                            row.food_rating
                          }
                        />
                      </p>

                      <p>
                        Service:{" "}
                        <Stars
                          value={
                            row.service_rating
                          }
                        />
                      </p>

                      <p>
                        Atmosphere:{" "}
                        <Stars
                          value={
                            row.atmosphere_rating
                          }
                        />
                      </p>

                      <p className="pt-2">
                        Return:{" "}
                        {row.would_return ===
                        null
                          ? "Not answered"
                          : row.would_return
                            ? "Yes"
                            : "Not sure"}
                      </p>

                      <p>
                        Contact allowed:{" "}
                        {row.permission_to_contact
                          ? "Yes"
                          : "No"}
                      </p>
                    </div>

                    <div className="space-y-4 text-xs leading-6 text-[#667067]">
                      {row.highlights && (
                        <div>
                          <strong className="text-[#314a3b]">
                            Enjoyed
                          </strong>
                          <p>
                            {row.highlights}
                          </p>
                        </div>
                      )}

                      {row.improvements && (
                        <div>
                          <strong className="text-[#314a3b]">
                            Improve
                          </strong>
                          <p>
                            {
                              row.improvements
                            }
                          </p>
                        </div>
                      )}

                      {row.additional_comments && (
                        <div>
                          <strong className="text-[#314a3b]">
                            Additional
                          </strong>
                          <p>
                            {
                              row.additional_comments
                            }
                          </p>
                        </div>
                      )}

                      {(row.email ||
                        row.phone) && (
                        <div className="border-t border-[#b5bbae] pt-3">
                          {row.email && (
                            <a
                              href={`mailto:${row.email}`}
                              className="block"
                            >
                              {row.email}
                            </a>
                          )}

                          {row.phone && (
                            <a
                              href={`tel:${row.phone}`}
                              className="block"
                            >
                              {row.phone}
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    <label>
                      <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8d988d]">
                        Status
                      </span>

                      <select
                        value={row.status}
                        onChange={(
                          event,
                        ) =>
                          void updateStatus(
                            row.id,
                            event.target
                              .value,
                          )
                        }
                        className="mt-3 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 py-3 text-xs outline-none"
                      >
                        {STATUSES.map(
                          (status) => (
                            <option
                              key={status}
                              value={status}
                            >
                              {status}
                            </option>
                          ),
                        )}
                      </select>
                    </label>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
